import { getShopItemPrice } from './shopItemsAndCart'
import { pathWithRegion } from './regions'

export const findItemByType = (itemType, shopItems) => {
  return shopItems.find(i => i.type === itemType)
}

export const findItemById = (id, shopItems) => {
  return shopItems.find(i => i.id === id)
}

export const getOptionalItems = (item, shopItems, availableOnly) => {
  return item.pairsWith && item.pairsWith.length
    ? item.pairsWith
      .map(id => shopItems.find(si => si.id === id))
      .filter(i => availableOnly ? !isOutOfStock(i) : true)
    : []
}

export const isItemAvailable = (item) => {
  if (item.availabilityStatus) {
    return item.availabilityStatus === 'AVAILABLE'
  }
  return item.price && item.available
}

// An item is "unavailable in the current region" when it exists in the catalog
// join (so findItemByType returns it, NOT null) but carries no price for the
// resolved region: the pricing backend returned no offer for it, so regionData
// stamps availabilityStatus 'UNAVAILABLE' and nulls every price object (e.g. an
// EU-only GT-2 rig viewed on /us or /au — rigs are EU-restricted, task #46/#137).
//
// This is DISTINCT from out-of-stock / sold-out, which KEEPS its price (so the
// page still shows the price + a "sold out" state). Detection mirrors the cart
// panel's region-unavailable check (a4d9eb5a: `!getShopItemPrice(item)`), so the
// cart and the product page agree on what "not available in your region" means.
// Region + availability are fixed server-side, so this is SSR-deterministic.
export const isUnavailableInRegion = (item) =>
  !!item && (item.availabilityStatus === 'UNAVAILABLE' || !getShopItemPrice(item))

// Whether a catalog item stays off the shop grids for this visitor. This is a
// deliberate catalog decision for retired items, never a stock rule: a sold-out
// item with neither field keeps its sold-out card.
//   - hiddenInRegions (e.g. ['eu']): hidden on those URL regions.
//   - onlyInShippingRegions (e.g. ['UK']): shown only once the offers on screen
//     were priced for one of those backend shipping regions. `shippingRegion` is
//     null on SSR and first paint, so these items are hidden until then.
export const isHiddenOnShop = (item, region, shippingRegion = null) => {
  if (region && Array.isArray(item.hiddenInRegions) && item.hiddenInRegions.includes(region)) {
    return true
  }
  const only = item.onlyInShippingRegions
  return Array.isArray(only) && only.length > 0 && !only.includes(shippingRegion)
}

export const isPreOrder = (item) => {
  return item.preOrderInfo && item.preOrderInfo.isPreOrderAvailable
}

export const anyItemIsOutOfStock = (items) => {
  return items.some(i => isOutOfStock(i))
}

export const allItemsAreAvailable = (items) => {
  return !anyItemIsOutOfStock(items)
}

export const isOutOfStock = (item) => {
  if (item.availabilityStatus === 'UNAVAILABLE' || item.availabilityStatus === 'SOLD_OUT') {
    return true
  }
  return !isItemAvailable(item) && !isPreOrder(item)
}

// The shop card skips the sold-out fade for a "new" item because a new item
// that is out of stock is one that has not been released yet ("Coming soon").
// An item the backend explicitly marks SOLD_OUT / UNAVAILABLE has been released
// and sold out, so it must fade like any other sold-out card even if it still
// carries the isNew flag (the R295 kept isNew after selling out and never faded).
export const isComingSoon = (item) => {
  if (!item.isNew) return false
  return item.availabilityStatus !== 'SOLD_OUT' && item.availabilityStatus !== 'UNAVAILABLE'
}

export const getOutOfStockMessage = (item) => {
  if (item.availabilityStatus === 'UNAVAILABLE') return 'Not available'
  if (item.availabilityStatus === 'SOLD_OUT') return 'Sold out'
  if (item.outOfStockMessage) return item.outOfStockMessage
  return item.isNew ? 'Coming soon' : 'Out of stock'
}

// Build the product checkout-page URL. When a `region` is supplied the link is
// region-scoped (e.g. /eu/product/pro-pedals) so navigating from the shop keeps
// the region instead of bouncing the bare /product/… URL through a 302 (bug #2).
// Product pages are always commerce, so we prefix unconditionally via
// pathWithRegion rather than the slug-list-gated regionalHref.
export const buildCheckoutPageUrlFromProductSetId = (productSetId, region) => {
  if (!productSetId) {
    return '#'
  }
  const path = `/product/${productSetId}`
  return region ? pathWithRegion(path, region) : path
}

export const sortByPrice = (items, descending) => {
  items.sort((a, b) => {
    const priceA = getShopItemPrice(a, true)
    const priceB = getShopItemPrice(b, true)
    if (descending) {
      return (priceB ? priceB.priceCents : 0) - (priceA ? priceA.priceCents : 0)
    } else {
      return (priceA ? priceA.priceCents : 0) - (priceB ? priceB.priceCents : 0)
    }
  })

  return items
}
